'use client';
import { CaretDown, CaretUp, Info, X } from '@phosphor-icons/react';
import React, { useState, useRef, useEffect } from 'react';
import IconWrapper from '../IconWrapper/IconWrapper';
import DropdownItem from '../DropdownItem/DropdownItem';
import { LanguageType, LanguageDirection } from '@/lib/types';
type InputFieldAppearance = "default" | "reversed";
type InputFieldState = "normal" | "disabled" | "filled" | "invalid";

interface DropdownOption {
	value: string;
	label: string;
	disabled?: boolean;
	icon?: React.ReactElement<{ color?: string, size?: number, className?: string }>;
}

interface DropdownFieldProps {
	label: string;
	size: number;
	hint?: string;
	options: DropdownOption[];
	language?: LanguageType;
	disabled?: boolean;
	invalid?: boolean;
	placeholder?: string;
	value?: string;
	onChange?: (value: string) => void;
	optional?: boolean;
	// "reversed" mirrors Figma's Style=Reversed variant (dark backgrounds).
	appearance?: InputFieldAppearance;
}

const LANGUAGE_TO_FLEX_PLACEMENT: Record<LanguageType, string> = {
	english: "start",
	arabic: "end"
};



const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

const SIZE_TO_TEXT_STYLE: Record<number, string> = {
	20: 'text-20-to-18--alexandria',
	18: 'text-18-fixed--alexandria',
};

const STATE_TO_BORDER: Record<InputFieldState, string> = {
	normal: `
		border-stroke-soft-200
		focus-within:border-theme-yellow-primary-base
	`,
	filled: `
		border-theme-yellow-primary-base
	`,
	disabled: `
		border-stroke-disabled
	`,
	invalid: `
		border-state-error-base
	`
};

const STATE_TO_LABEL_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-tertiary
		group-hover:text-text-quinary
	`,
	filled: `
		text-text-tertiary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-tertiary
	`,
};

const STATE_TO_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-disabled
	`,
	filled: `
		text-text-primary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-primary
	`,
};

const STATE_TO_HINT_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-quaternary
	`,
	filled: `
		text-text-quaternary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-state-error-base
	`,
};

const STATE_TO_ICON_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-graphics-quaternary
	`,
	filled: `
		text-graphics-quaternary
	`,
	disabled: `
		text-graphics-disabled
	`,
	invalid: `
		text-state-error-base
	`,
};

// Color rules for the selected-option icon that sits next to the trigger text.
// Mirrors STATE_TO_INPUT_ICON_TEXT from TextInputField — distinct from
// STATE_TO_ICON_TEXT (which colors the hint-row Info icon).
const STATE_TO_INPUT_ICON_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-graphics-tertiary
		group-hover:opacity-50
	`,
	filled: `
		text-graphics-primary
	`,
	disabled: `
		text-graphics-disabled
	`,
	invalid: `
		text-graphics-primary
	`,
};


// Figma "Style=Reversed" variant - for fields sitting on dark surfaces.
const REVERSED_STATE_TO_BORDER: Record<InputFieldState, string> = {
	normal: `
		border-stroke-soft-200
		focus-within:border-theme-yellow-primary-light
	`,
	filled: `
		border-theme-yellow-primary-light
	`,
	disabled: `
		border-stroke-disabled
	`,
	invalid: `
		border-state-error-light
	`
}

const REVERSED_STATE_TO_LABEL_TEXT: Record<InputFieldState, string> = {
	normal: `text-text-reversed`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-text-reversed`,
}

const REVERSED_STATE_TO_TEXT: Record<InputFieldState, string> = {
	normal: `text-text-disabled`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-text-reversed`,
}

const REVERSED_STATE_TO_HINT_TEXT: Record<InputFieldState, string> = {
	normal: `text-text-reversed`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-state-error-light`,
}

const REVERSED_STATE_TO_ICON_TEXT: Record<InputFieldState, string> = {
	normal: `text-graphics-reversed`,
	filled: `text-graphics-reversed`,
	disabled: `text-graphics-disabled`,
	invalid: `text-state-error-light`,
}

const REVERSED_STATE_TO_INPUT_ICON_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-graphics-reversed
		group-hover:opacity-50
	`,
	filled: `text-graphics-reversed`,
	disabled: `text-graphics-disabled`,
	invalid: `text-graphics-reversed`,
}

type InputFieldStyles = {
	border: Record<InputFieldState, string>
	label: Record<InputFieldState, string>
	text: Record<InputFieldState, string>
	hint: Record<InputFieldState, string>
	icon: Record<InputFieldState, string>
	inputIcon: Record<InputFieldState, string>
	asterisk: string
}

const APPEARANCE_TO_STYLES: Record<InputFieldAppearance, InputFieldStyles> = {
	default: {
		border: STATE_TO_BORDER,
		label: STATE_TO_LABEL_TEXT,
		text: STATE_TO_TEXT,
		hint: STATE_TO_HINT_TEXT,
		icon: STATE_TO_ICON_TEXT,
		inputIcon: STATE_TO_INPUT_ICON_TEXT,
		asterisk: 'text-theme-yellow-primary-base',
	},
	reversed: {
		border: REVERSED_STATE_TO_BORDER,
		label: REVERSED_STATE_TO_LABEL_TEXT,
		text: REVERSED_STATE_TO_TEXT,
		hint: REVERSED_STATE_TO_HINT_TEXT,
		icon: REVERSED_STATE_TO_ICON_TEXT,
		inputIcon: REVERSED_STATE_TO_INPUT_ICON_TEXT,
		asterisk: 'text-theme-yellow-primary-light',
	},
}

const getState = (disabled: boolean, filled: boolean, invalid: boolean): InputFieldState => {
	if (disabled) return "disabled";
	if (invalid) return "invalid";
	if (filled) return "filled";
	return "normal";
};


const DropdownField: React.FC<DropdownFieldProps> = ({
	label,
	size,
	hint,
	options,
	language = "english",
	disabled = false,
	placeholder = "Select an option...",
	invalid = false,
	value,
	onChange,
	optional = false,
	appearance = "default",
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null);
	const isFilled = selectedOption !== null;
	const containerRef = useRef<HTMLDivElement>(null);
	const styles = APPEARANCE_TO_STYLES[appearance];

	// Sync internal state with value prop
	useEffect(() => {
		if (value === '' || value === undefined || value === null) {
			setSelectedOption(null);
		} else {
			const option = options.find(opt => opt.value === value);
			if (option) {
				setSelectedOption(option);
			}
		}
	}, [value, options]);

	// useEffect(() => {
	// 	const handleClickOutside = (event: MouseEvent) => {
	// 		if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
	// 			handleClose();
	// 		}
	// 	};
	// 	document.addEventListener('mousedown', handleClickOutside);
	// 	return () => document.removeEventListener('mousedown', handleClickOutside);
	// }, []);

	const handleClose = () => {
		setIsClosing(true);
		setTimeout(() => {
			setIsOpen(false);
			setIsClosing(false);
		}, 300);
	};

	const handleSelect = (option: DropdownOption) => {
		setSelectedOption(option);
		handleClose();
		onChange?.(option.value);
	};

	const handleClear = () => {
		setIsOpen(false);
		setSelectedOption(null);
		onChange?.('');
	};

	return (
		<div ref={containerRef} className="w-full flex flex-col gap-[0.75rem] group">
			<div
				className={`
					flex flex-col gap-[1rem]
					border-b-main
					${styles.border[getState(disabled, isFilled, invalid)]}
					${`items-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
				`}
			>
				<div
					style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
					className={`
						flex flex-row gap-[0.125rem]
						items-center
						${`justify-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
					`}
				>
				<label
					className={`
						text-16-fixed--alexandria
						transition-colors duration-300 ease-in-out
						${styles.label[getState(disabled, isFilled, invalid)]}
					`}
				>
						{label}
					</label>
					{!optional && <p className={styles.asterisk}>*</p>}
				</div>
				<div className="relative w-full">
					<div
						style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
						className='
					    flex items-center justify-center
 						pb-[1rem]
					'>
						<button
							disabled={disabled}
							onClick={() => !disabled && setIsOpen(!isOpen)}
							className={`
								cursor-pointer
							    w-full
								flex-grow
								flex flex-row items-center gap-[0.25rem]
							    focus:outline-none focus:ring-0
								transition-all duration-300 ease-in-out
						              ${SIZE_TO_TEXT_STYLE[size]}
						              ${styles.text[getState(disabled, isFilled, invalid)]}
					            `}
						>
							{selectedOption?.icon &&
								<div className="p-[0.25rem]">
									<IconWrapper
										size={20}
										className={`
											transition-[color,opacity] duration-300 ease-in-out
											${styles.inputIcon[getState(disabled, isFilled, invalid)]}
										`}
									>
										{selectedOption.icon}
									</IconWrapper>
								</div>
							}
							<span className={`transition-all duration-300 ease-in-out ${SIZE_TO_TEXT_STYLE[size]} ${!selectedOption ? 'text-text-disabled' : ''}`}>
								{selectedOption?.label || placeholder || 'Select an option...'}
							</span>
						</button>

						{isFilled ?
							<button className='
								flex items-center justify-center flex-row gap-[0.25rem]
							    focus:outline-none focus:ring-0
								group/clear
								'>
								<p className={`
									text-12-fixed--alexandria
									cursor-pointer 
									${appearance === 'reversed' ? 'text-text-disabled' : `text-text-tertiary 
									hover:text-graphics-quaternary
									group-hover/clear:text-graphics-quaternary`}
								`} onClick={(e) => {
										e.stopPropagation();
										handleClear();
									}}
								>
									Clear
								</p>
								<div className='p-[0.25rem]'>
									<X size={20}
										className={`
										cursor-pointer 
										${appearance === 'reversed' ? 'text-graphics-reversed' : `text-graphics-secondary
										hover:text-graphics-quaternary
										disabled:text-graphics-disabled
										group-hover/clear:text-graphics-quaternary`}
										`}
										onClick={(e) => {
											e.stopPropagation();
											handleClear();
										}}
									/>
								</div>
							</button>
							:
							isOpen ?
								<button
									disabled={disabled}
									onClick={() => !disabled && setIsOpen(!isOpen)}
									className={`
									cursor-pointer
									p-[0.25rem]
									${appearance === 'reversed' ?
											'text-graphics-reversed disabled:text-graphics-disabled' :
											isOpen ?
											'text-graphics-secondary' :
											`
										text-graphics-quaternary
										focus:text-graphics-quaternary
										hover:text-graphics-quaternary
										group-hover:text-graphics-quaternary
										disabled:text-graphics-disabled
										`}
									`}>
									<CaretUp size={20} />
								</button> :
								<button
									disabled={disabled}
									onClick={() => !disabled && setIsOpen(!isOpen)}
									className={`
									cursor-pointer
									p-[0.25rem]
									${appearance === 'reversed' ?
											'text-graphics-reversed disabled:text-graphics-disabled' :
											isOpen ?
											'text-graphics-secondary' :
											`
										text-graphics-quaternary
										focus:text-graphics-quaternary
										hover:text-graphics-quaternary
										group-hover:text-graphics-quaternary
										disabled:text-graphics-disabled
										`}
									`}>
									<CaretDown size={20} />
								</button>
						}
					</div>

					{
						(isOpen || isClosing) && !disabled && (
							<div className={`
							absolute z-10
							w-full
							mt-0
							${appearance === 'reversed' ? 'bg-creams-c-800' : 'bg-bg-light-primary'}
							border-main 
							border-stroke-soft-200
							border-t-theme-yellow-primary-base
							rounded-b-[16px]
							px-[0.625rem]
							py-[0.75rem]
							flex flex-col gap-[0.5rem]
							${isClosing ? 'animate-dropdown-close' : 'animate-dropdown-open'}
							origin-top
						`}>
								{options.map((option, index) => (
									<div
										key={option.value}
										style={{
											// Long-form only — mixing the `animation` shorthand with
											// `animationDelay` causes React's style reconciler to warn,
											// because the shorthand also resets delay to 0 and the apply
											// order isn't guaranteed.
											animationName: isClosing ? 'dropdown-item-disappear' : 'dropdown-item-appear',
											animationDuration: '500ms',
											animationTimingFunction: 'ease-out',
											animationFillMode: 'forwards',
											animationDelay: `${isClosing ? ((options.length - 1 - index) * 50) : (index * 50)}ms`,
										}}
									>
									<DropdownItem
										option={option}
										selected={selectedOption?.value === option.value}
										language={language}
										onClick={() => handleSelect(option)}
										size={size}
										appearance={appearance}
									/>
									</div>
								))}
							</div>
						)
					}
				</div >
			</div >
			<div
				style={{
					direction: LANGUAGE_TO_DIRECTION[language],
					paddingInlineEnd: '2rem',
					height: '1rem'
				}}
				className={`
				flex 
				flex-row 
				gap-[0.25rem] 
				items-center 
				justify-start
				transition-opacity duration-300 ease-in-out
				${hint ? 'opacity-100' : 'opacity-0'}
			`}
			>
				{hint && <IconWrapper className={`${styles.icon[getState(disabled, isFilled, invalid)]} self-center`} size={16}>
					<Info />
				</IconWrapper>}
				<p
					className={`
						text-12-fixed--alexandria
						${styles.hint[getState(disabled, isFilled, invalid)]}
					`}
				>
					{hint || " "}
				</p>
			</div>
		</div >
	);
};

export default DropdownField;
